import type { ComponentProps, ReactNode } from 'react'
import { ImageCompany } from '@/routes/_app/-components/assets/image-company'
import { cn, getInitials } from '@/lib/utils'
import type { ProfileType } from 'shared'
import { useLingui } from '@lingui/react/macro'
import { cva, type VariantProps } from 'class-variance-authority'
import { NAVIGATION_ICONS } from './navigation-config'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

// For avatar use the expertly-crafted default color palette for tailwind: https://tailwindcss.com/docs/customizing-colors
const personColors = [
  '#ef4444', // Red
  '#f97316', // Orange
  '#f59e0b', // Amber
  '#eab308', // Yellow
  '#84cc16', // Lime
  '#22c55e', // Green
  '#10b981', // Emerald
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#0ea5e9', // Sky
  '#3b82f6', // Blue
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#a855f7', // Purple
  '#d946ef', // Fuchsia
  '#ec4899', // Pink
  '#f43f5e', // Rose
].reverse()

// Background color classes for non-person profile types
// Each type/market gets a distinct amber shade to differentiate them
const profileTypeColorClasses: Record<string, string> = {
  job: 'bg-blue-500/85', // Blue with transparency
  market_job: 'bg-amber-400/90',
  market_applicant: 'bg-amber-400/90',
  market: 'bg-amber-400/90',
  market_company: 'bg-amber-400/90',
  market_company_job: 'bg-amber-400/90',
  company: 'bg-slate-500/85', // Slate with transparency
  team: 'bg-violet-500/85', // Violet with transparency
  org_group: 'bg-emerald-500/85', // Slate with transparency
  public: 'bg-emerald-500/85', // Slate with transparency
}

const variantColorClasses: Record<string, string> = {
  job: 'bg-indigo-500/85',
  org: 'bg-emerald-500/85',
}

// Subtle (resting) class strings used inside CollapsibleListItem rows.
// At rest the avatar uses a 10% tint of the entity color with muted text.
// When an ancestor `Item` (group/item) carries data-selected=true, the brand
// color is restored. Tailwind requires static class names — keep these literal.
const subtleClassMap: Record<string, string> = {
  indigo:
    'bg-indigo-500/10 [&>span]:text-indigo-400 dark:[&>span]:text-indigo-500 group-data-[selected=true]/item:bg-indigo-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  blue: 'bg-blue-500/10 [&>span]:text-blue-400 dark:[&>span]:text-blue-500 group-data-[selected=true]/item:bg-blue-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  violet:
    'bg-violet-500/10 [&>span]:text-violet-400 dark:[&>span]:text-violet-500 group-data-[selected=true]/item:bg-violet-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  emerald:
    'bg-emerald-500/10 [&>span]:text-emerald-400 dark:[&>span]:text-emerald-500 group-data-[selected=true]/item:bg-emerald-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  slate:
    'bg-slate-500/10 [&>span]:text-slate-400 dark:[&>span]:text-slate-500 group-data-[selected=true]/item:bg-slate-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  amber:
    'bg-amber-400/10 [&>span]:text-amber-500 dark:[&>span]:text-amber-400 group-data-[selected=true]/item:bg-amber-400/90 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
}

const getSubtleColorName = (type?: ProfileType, variant?: string): string => {
  if (variant === 'job') return 'indigo'
  if (variant === 'org') return 'emerald'
  if (type === 'job') return 'blue'
  if (type === 'team') return 'violet'
  if (type === 'org_group' || type === 'public') return 'emerald'
  if (type === 'company') return 'slate'
  if (
    type === 'market_job' ||
    type === 'market_applicant' ||
    type === 'market' ||
    type === 'market_company' ||
    type === 'market_company_job'
  )
    return 'amber'
  return 'slate'
}

const hashString = (string: string) => {
  let hash = 0
  for (let i = 0; i < string.length; ++i) hash = string.charCodeAt(i) + ((hash << 5) - hash)
  return hash
}

const stringToColor = (string: string, isPerson: boolean) => {
  if (!isPerson) return undefined
  const hash = hashString(string)
  return personColors[Math.abs(hash % personColors.length)]
}

const getColorClass = (type?: ProfileType, variant?: string) => {
  if (variant && variant in variantColorClasses) {
    return variantColorClasses[variant]
  }
  if (type && type in profileTypeColorClasses) {
    return profileTypeColorClasses[type]
  }
  return 'bg-slate-500/85'
}

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden items-center justify-center font-medium text-white select-none rounded-md',
  {
    variants: {
      size: {
        'xs': 'size-5 text-xs rounded-full',
        'sm': 'size-6 text-sm rounded-sm',
        'md': 'size-8 text-lg',
        'lg': 'size-9 text-xl',
        'xl': 'size-10 text-2xl',
        '2xl': 'size-11 text-3xl',
        '3xl': 'size-12 text-4xl rounded-lg',
        '4xl': 'size-16 text-4xl rounded-lg',
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
)

function withTooltip(element: React.ReactElement, tooltip?: ReactNode) {
  if (!tooltip) return element
  return (
    <Tooltip>
      <TooltipTrigger render={element} />
      <TooltipContent side="bottom" className="p-2">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

export function Avatar({
  size = 'lg',
  url,
  type,
  name,
  className,
  variant,
  forceInitials,
  subtle,
  tooltip,
  ...props
}: Omit<ComponentProps<'span'>, 'style'> & {
  size?: VariantProps<typeof avatarVariants>['size']
  url?: string | null
  type?: ProfileType
  name?: string | null
  variant?: 'job' | 'org'
  /** When true, render only initials — skip the type icon and the URL image. */
  forceInitials?: boolean
  /**
   * When true, render with a 10% tint of the entity color at rest and restore
   * the full brand color when an ancestor `Item` has data-selected=true.
   * Designed for avatars inside CollapsibleListItem rows.
   */
  subtle?: boolean
  tooltip?: ReactNode
}) {
  const { t } = useLingui()
  const isPerson = type === 'applicant' || type === 'member' || type === 'employee' || type === 'org_user'
  const id = `${isPerson ? 'applicant' : type}-${name}`
  const background = stringToColor(id, isPerson)
  const colorClass = !isPerson ? getColorClass(type, variant) : undefined
  const subtleClass = subtle && !isPerson ? subtleClassMap[getSubtleColorName(type, variant)] : undefined
  const initials = getInitials(name ?? '', type ?? '')

  const getIconComponent = () => {
    // An applicant still registering has no image and no name yet — show the
    // generic applicant icon instead of empty initials.
    if (type === 'applicant' && !name) return NAVIGATION_ICONS.applicant
    if (type === 'market_job') return NAVIGATION_ICONS.marketOffer
    if (type === 'market_applicant') return NAVIGATION_ICONS.marketApplicant
    if (type === 'company') return null // Uses ImageCompany instead
    if (variant === 'job') return NAVIGATION_ICONS.offer
    if (type === 'job') return NAVIGATION_ICONS.job
    if (type === 'team') return NAVIGATION_ICONS.team
    if (type === 'org_group') return NAVIGATION_ICONS.orgGroup
    if (type === 'market') return NAVIGATION_ICONS.market
    if (type === 'market_company') return NAVIGATION_ICONS.market
    if (type === 'market_company_job') return NAVIGATION_ICONS.market
    return null
  }

  const IconComponent = getIconComponent()

  // Company logo / entity icon / initials on the colored background: the resting content for image-less
  // avatars, and what shows through until the photo below arrives. `forceInitials` shows only the initials.
  const fallbackContent = forceInitials ? (
    <span className={cn(!isPerson && 'text-white text-xs font-bold')}>{initials}</span>
  ) : type === 'company' ? (
    <>
      <ImageCompany className="fill-white @max-[100px]/panel:hidden" width="45%" height="45%" />
      <span className="hidden text-xs font-bold text-white @max-[100px]/panel:block">{initials}</span>
    </>
  ) : IconComponent ? (
    <>
      {/* oxlint-disable-next-line react/static-components -- `IconComponent` is a per-type LOOKUP from a
          static map, not a component defined inline; its identity is stable for a given entity type. */}
      <IconComponent className="text-white @max-[100px]/panel:hidden" width="45%" height="45%" />
      {/* When collapsed into the icon-rail (ancestor `@container/squash` ≤ 100px), show initials
          instead of the generic entity icon so items are distinguishable. */}
      <span className="hidden text-xs font-bold text-white @max-[100px]/panel:block">{initials}</span>
    </>
  ) : (
    <span className={cn(!isPerson && 'text-white font-bold')}>{initials}</span>
  )

  return withTooltip(
    <span
      {...props}
      role="img"
      aria-label={t`User avatar`}
      className={cn(avatarVariants({ size }), subtleClass ?? colorClass, className)}
      style={background ? { background } : undefined}
    >
      {fallbackContent}
      {/* A BACKGROUND, not an <img>: one that 404s or is still in flight paints nothing, so the browser
          alone decides whether the photo or the initials underneath show — first frame of the SSR HTML,
          nothing waiting on hydration. An <img> has to be hidden by React until `onLoad`, because Chromium
          paints its broken-image glyph even with alt="" — and that gate is what issue #76 came out of. */}
      {!forceInitials && !!url && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-[inherit] bg-cover bg-center"
          style={{ backgroundImage: `url("${url.replace(/["\\]/g, encodeURIComponent)}")` }}
        />
      )}
    </span>,
    tooltip,
  )
}
